/**
 * Sync state.
 *
 * FR-PLT-05 requires unsynced entries to be surfaced clearly, and WD-04 requires it in
 * two places: a global indicator and a per-row marker. This module owns the global half.
 *
 * **The authority is Firestore's own `metadata.hasPendingWrites`,** not `navigator.onLine`
 * and not a count of in-flight promises. Both of those were tried and both lie:
 *
 *   · `navigator.onLine` reports "online" whenever a network interface exists, including
 *     a wifi connection with no route to the internet.
 *   · Counting unresolved write promises misses the case that matters most. Offline,
 *     Firestore applies a write locally and its promise may settle before the server has
 *     ever seen it — so the counter returns to zero while the data is still only on the
 *     device.
 *
 * Snapshot metadata is the one signal that knows the difference, so the data layer feeds
 * it in from its listeners.
 */

export type SyncStatus = 'synced' | 'pending' | 'offline';

export interface SyncState {
  status: SyncStatus;
  /** Sources currently holding unsent writes. */
  pendingSources: number;
  online: boolean;
  /** True when Firestore says the snapshot came from cache rather than the server. */
  fromCache: boolean;
}

type Listener = (state: SyncState) => void;

const listeners = new Set<Listener>();
const pending = new Map<string, boolean>();
const cached = new Map<string, boolean>();
let online = typeof navigator === 'undefined' ? true : navigator.onLine;

function snapshot(): SyncState {
  const pendingSources = [...pending.values()].filter(Boolean).length;
  const fromCache = [...cached.values()].some(Boolean);

  // Pending writes outrank everything: "3 pending" is more useful than "offline",
  // because it says what is at stake rather than what the radio is doing.
  const status: SyncStatus =
    pendingSources > 0 ? 'pending' : !online || fromCache ? 'offline' : 'synced';

  return { status, pendingSources, online, fromCache };
}

function emit(): void {
  const state = snapshot();
  for (const listener of listeners) listener(state);
}

export function getSyncState(): SyncState {
  return snapshot();
}

export function watchSyncState(listener: Listener): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
}

/**
 * Report what a Firestore listener's snapshot metadata says.
 *
 * Called from every `onSnapshot` in the data layer, which must pass
 * `{ includeMetadataChanges: true }` — without it the callback does not fire when only
 * the pending-write flag changes, and the indicator never updates.
 */
export function reportSnapshotMetadata(
  source: string,
  metadata: { hasPendingWrites: boolean; fromCache: boolean },
): void {
  const changed =
    pending.get(source) !== metadata.hasPendingWrites || cached.get(source) !== metadata.fromCache;
  pending.set(source, metadata.hasPendingWrites);
  cached.set(source, metadata.fromCache);
  if (changed) emit();
}

/** Drop a source when its listener unsubscribes, so a stale flag cannot linger. */
export function releaseSource(source: string): void {
  const had = pending.get(source) || cached.get(source);
  pending.delete(source);
  cached.delete(source);
  if (had) emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    online = true;
    emit();
  });
  window.addEventListener('offline', () => {
    online = false;
    emit();
  });
}

/**
 * Pass a write through unchanged.
 *
 * Kept as the single place every write goes through, so failures have one place to be
 * observed. It deliberately no longer drives the indicator — see the note at the top of
 * this file for why an in-flight counter was the wrong signal.
 */
export async function trackWrite<T>(operation: Promise<T>): Promise<T> {
  return operation;
}

/** Test seam. Not called by application code. */
export function __resetSyncState(): void {
  pending.clear();
  cached.clear();
  online = true;
  listeners.clear();
}
